/**
 * ContextPacketPreview (D2) — a button that opens a dialog previewing the
 * resolved Context Packet v0.2 for a context. Calls `GET /api/contexts/:id/resolve`
 * (CONTRACT_V2 §3.4), which synthesizes a minimal resolve input and returns the
 * packet JSON *without* persisting it to R2 (preview only, D3-advisory).
 *
 * The dialog summarizes the human-meaningful parts of the packet — coverage,
 * policy, post-budget active contexts (scored), and the selected packages — then
 * exposes the full packet JSON via the shared {@link JsonViewer}. This answers
 * "what would an agent actually see when operating in this context?".
 */
import * as React from "react";
import { FileJson, Loader2, Sparkles } from "@/components/ui/icon";

import type { ContextPacketV2 } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { JsonViewer } from "@/components/app/JsonViewer";
import { api } from "@/lib/api";
import { humanize } from "@/lib/format";

import { MaturityBadge } from "./MaturityBadge";

export interface ContextPacketPreviewProps {
  /** The context id to resolve a preview packet for. */
  contextId: string;
}

/** Lazily fetch the resolved packet the first time the dialog opens. */
function usePacketPreview(contextId: string, open: boolean) {
  const [packet, setPacket] = React.useState<ContextPacketV2 | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || packet || loading) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<ContextPacketV2>(`/contexts/${encodeURIComponent(contextId)}/resolve`)
      .then((data) => {
        if (!cancelled) setPacket(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Failed to resolve context packet.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, contextId, packet, loading]);

  return { packet, loading, error };
}

/** Small labelled chip list (e.g. selected frames / principles / bets). */
function PackageRefs({ label, ids }: { label: string; ids: string[] }) {
  if (ids.length === 0) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {ids.map((id) => (
          <Badge key={id} variant="outline" className="font-mono text-[11px]">
            {id}
          </Badge>
        ))}
      </div>
    </div>
  );
}

/** Renders the human-meaningful summary of a resolved packet. */
function PacketSummary({ packet }: { packet: ContextPacketV2 }) {
  const coveragePct = Math.round((packet.coverage ?? 1) * 100);
  const pkgs = packet.selected_packages;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">Policy: {humanize(packet.policy)}</Badge>
        <Badge variant={coveragePct >= 100 ? "success" : "warning"}>
          Coverage {coveragePct}%
        </Badge>
        <Badge variant="outline" className="font-mono text-[11px]">
          v{packet.version}
        </Badge>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Active contexts ({packet.active_contexts.length})
        </p>
        {packet.active_contexts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No contexts survived the budget.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {packet.active_contexts.map((ctx) => (
              <li
                key={ctx.id}
                className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{ctx.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {ctx.type ? humanize(ctx.type) : "—"}
                    {ctx.maturity_stage
                      ? ` · ${humanize(ctx.maturity_stage)}`
                      : ""}
                  </p>
                </div>
                {ctx.maturity_stage ? (
                  <MaturityBadge stage={ctx.maturity_stage} />
                ) : null}
                {typeof ctx.score === "number" ? (
                  <span className="shrink-0 font-mono text-xs text-muted-foreground">
                    {ctx.score.toFixed(1)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {pkgs ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {pkgs.skill ? (
            <PackageRefs label="Skill" ids={[pkgs.skill]} />
          ) : null}
          {pkgs.contour ? (
            <PackageRefs label="Contour" ids={[pkgs.contour]} />
          ) : null}
          <PackageRefs label="Frames" ids={pkgs.frames ?? []} />
          <PackageRefs label="Principles" ids={pkgs.principles ?? []} />
          <PackageRefs label="Bets" ids={pkgs.bets ?? []} />
        </div>
      ) : null}

      {packet.constraints.length > 0 ? (
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Constraints
          </p>
          <ul className="list-inside list-disc space-y-0.5 text-sm">
            {packet.constraints.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <FileJson className="h-3.5 w-3.5" /> Full packet
        </p>
        <JsonViewer data={packet} maxHeightClassName="max-h-[24rem]" />
      </div>
    </div>
  );
}

/** "Preview packet" trigger + dialog (CONTRACT_V2 §3.4 resolve preview). */
export function ContextPacketPreview({ contextId }: ContextPacketPreviewProps) {
  const [open, setOpen] = React.useState(false);
  const { packet, loading, error } = usePacketPreview(contextId, open);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Sparkles className="mr-2 h-4 w-4" /> Preview packet
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Context Packet preview</DialogTitle>
          <DialogDescription>
            The resolved Context Packet v0.2 an agent would receive when
            operating in this context. Preview only — not stored.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            <span>Resolving packet…</span>
          </div>
        ) : error ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive"
          >
            <p className="font-medium">Failed to resolve</p>
            <p className="mt-1 text-destructive/80">{error}</p>
          </div>
        ) : packet ? (
          <PacketSummary packet={packet} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default ContextPacketPreview;
